"use strict";
/**
 * Ensure an existing NetSuite customer allows a transaction currency on currencyList.
 * Mirrors finance-centaur cleverbridge_import.ensure_existing_customer_has_purchase_currency.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.createCustomerCurrencyEnsurer = exports.normalizeCurrencyInternalId = exports.CUSTOMER_CURRENCY_PATCH_RETRY_PAUSE_MS = exports.CUSTOMER_CURRENCY_PATCH_MAX_ATTEMPTS = void 0;
exports.CUSTOMER_CURRENCY_PATCH_MAX_ATTEMPTS = 5;
exports.CUSTOMER_CURRENCY_PATCH_RETRY_PAUSE_MS = 10_000;
const LOG_PREFIX = "[IC-ENTITY][CUSTOMER_CURRENCY]";
function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
/** Strip suitetalk host so `fetchData` can append the path to the REST base URL. */
function restPathFromNetSuiteHref(href) {
    const trimmed = href.trim();
    const marker = "suitetalk.api.netsuite.com";
    const idx = trimmed.indexOf(marker);
    if (idx >= 0) {
        return trimmed.slice(idx + marker.length);
    }
    if (trimmed.startsWith("/services/rest/")) {
        return trimmed;
    }
    return trimmed;
}
function netsuiteRestSelfHref(links) {
    if (!Array.isArray(links))
        return null;
    for (const L of links) {
        if (L &&
            typeof L === "object" &&
            L.rel === "self" &&
            L.href) {
            return String(L.href).trim();
        }
    }
    return null;
}
function normalizeCurrencyInternalId(value) {
    if (value == null)
        return null;
    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
        const id = value.id;
        if (id == null)
            return null;
        return normalizeCurrencyInternalId(id);
    }
    if (typeof value === "boolean")
        return null;
    if (typeof value === "number" && Number.isFinite(value)) {
        return String(Math.trunc(value));
    }
    const s = String(value).trim();
    if (!s)
        return null;
    const n = Number(s);
    if (!Number.isFinite(n))
        return s;
    return String(Math.trunc(n));
}
exports.normalizeCurrencyInternalId = normalizeCurrencyInternalId;
function currencyInternalIdsEqual(a, b) {
    const na = normalizeCurrencyInternalId(a);
    const nb = normalizeCurrencyInternalId(b);
    if (na == null || nb == null)
        return false;
    return na === nb;
}
function currencyIdFromCurrencyListLinePayload(payload) {
    if (!payload || typeof payload !== "object")
        return null;
    const c = payload.currency;
    if (c && typeof c === "object" && !Array.isArray(c)) {
        const raw = c.id;
        if (raw != null && String(raw).trim() !== "") {
            return String(raw).trim();
        }
    }
    return null;
}
function purchaseCurrencyInCustomerList(purchaseCurrencyId, currencyIds) {
    return currencyIds.some((x) => currencyInternalIdsEqual(purchaseCurrencyId, x));
}
function createCustomerCurrencyEnsurer(deps) {
    const getCustomerPrimaryCurrencySuiteql = async (customerId) => {
        const raw = String(customerId).trim();
        if (!/^\d+$/.test(raw)) {
            console.warn(`${LOG_PREFIX} invalid customer id for SuiteQL: ${customerId}`);
            return null;
        }
        const q = `SELECT id, currency FROM customer WHERE id = ${raw}`;
        console.log(`${LOG_PREFIX} SuiteQL primary currency: ${q}`);
        const result = (await deps.executeSuiteQL(q));
        const rows = Array.isArray(result?.items) ? result.items : [];
        if (rows.length === 0) {
            console.log(`${LOG_PREFIX} no customer row for id=${raw}`);
            return null;
        }
        const row0 = rows[0];
        return normalizeCurrencyInternalId(row0?.currency);
    };
    const getCustomerCurrencyList = async (customerId) => {
        const cid = String(customerId).trim();
        const path = `/services/rest/record/v1/customer/${cid}/currencyList`;
        console.log(`${LOG_PREFIX} GET ${path}`);
        const data = (await deps.fetchData(path));
        const items = Array.isArray(data?.items) ? data.items : [];
        const out = [];
        for (const row of items) {
            if (!row || typeof row !== "object")
                continue;
            let curId = currencyIdFromCurrencyListLinePayload(row);
            if (curId) {
                out.push(curId);
                continue;
            }
            const lineHref = netsuiteRestSelfHref(row.links);
            if (!lineHref)
                continue;
            const linePath = restPathFromNetSuiteHref(lineHref);
            console.log(`${LOG_PREFIX} GET line ${linePath} (from href)`);
            const lineBody = await deps.fetchData(linePath);
            if (!lineBody || typeof lineBody !== "object") {
                throw new Error(`${LOG_PREFIX} currencyList line response is not a JSON object`);
            }
            curId = currencyIdFromCurrencyListLinePayload(lineBody);
            if (!curId) {
                throw new Error(`${LOG_PREFIX} currencyList line has no currency.id after GET`);
            }
            out.push(curId);
        }
        const seen = new Set();
        const unique = [];
        for (const x of out) {
            if (!seen.has(x)) {
                seen.add(x);
                unique.push(x);
            }
        }
        return unique;
    };
    const patchCustomerAddCurrency = async (customerId, currencyId) => {
        const cid = String(customerId).trim();
        const cur = String(currencyId).trim();
        const path = `/services/rest/record/v1/customer/${cid}`;
        const body = { currencyList: { items: [{ currency: { id: cur } }] } };
        console.log(`${LOG_PREFIX} PATCH ${path} currency_id=${cur}`);
        await deps.patchData(path, body);
    };
    const ensureCustomerHasCurrency = async (customerId, subscriptionCurrencyId, options) => {
        const cid = String(customerId).trim();
        const pc = normalizeCurrencyInternalId(subscriptionCurrencyId);
        if (!pc) {
            throw new Error(`${LOG_PREFIX} invalid subscription currency id: ${subscriptionCurrencyId}`);
        }
        const role = options?.role ? ` role=${options.role}` : "";
        const primary = await getCustomerPrimaryCurrencySuiteql(cid);
        console.log(`${LOG_PREFIX} customer=${cid}${role} primary_currency=${primary ?? "(none)"} subscription_currency=${pc}`);
        if (primary != null && currencyInternalIdsEqual(primary, pc)) {
            console.log(`${LOG_PREFIX} customer=${cid}${role} primary matches subscription currency — skip list/PATCH`);
            return {
                customerId: cid,
                currencyId: pc,
                outcome: "SKIP_PRIMARY_MATCH",
                patchAttempts: 0,
            };
        }
        const ids = await getCustomerCurrencyList(cid);
        console.log(`${LOG_PREFIX} customer=${cid}${role} currency id list: ${JSON.stringify(ids)}`);
        if (purchaseCurrencyInCustomerList(pc, ids)) {
            console.log(`${LOG_PREFIX} customer=${cid}${role} subscription currency already in customer currency list`);
            return {
                customerId: cid,
                currencyId: pc,
                outcome: "SKIP_ALREADY_IN_LIST",
                patchAttempts: 0,
            };
        }
        for (let attempt = 1; attempt <= exports.CUSTOMER_CURRENCY_PATCH_MAX_ATTEMPTS; attempt++) {
            console.log(`${LOG_PREFIX} customer=${cid}${role} PATCH add subscription currency attempt ${attempt}/${exports.CUSTOMER_CURRENCY_PATCH_MAX_ATTEMPTS}`);
            try {
                await patchCustomerAddCurrency(cid, pc);
            }
            catch (patchErr) {
                const detail = patchErr instanceof Error ? patchErr.message : String(patchErr);
                console.error(`${LOG_PREFIX} customer=${cid}${role} PATCH attempt ${attempt} failed: ${detail}`);
                if (attempt >= exports.CUSTOMER_CURRENCY_PATCH_MAX_ATTEMPTS) {
                    throw new Error(`${LOG_PREFIX} PATCH failed for customer ${cid} currency ${pc} after ${exports.CUSTOMER_CURRENCY_PATCH_MAX_ATTEMPTS} attempt(s): ${detail}`);
                }
                console.log(`${LOG_PREFIX} waiting ${exports.CUSTOMER_CURRENCY_PATCH_RETRY_PAUSE_MS / 1000}s before PATCH retry...`);
                await sleep(exports.CUSTOMER_CURRENCY_PATCH_RETRY_PAUSE_MS);
                continue;
            }
            const idsAfter = await getCustomerCurrencyList(cid);
            console.log(`${LOG_PREFIX} customer=${cid}${role} currency id list after PATCH: ${JSON.stringify(idsAfter)}`);
            if (purchaseCurrencyInCustomerList(pc, idsAfter)) {
                console.log(`${LOG_PREFIX} customer=${cid}${role} subscription currency confirmed on customer`);
                return {
                    customerId: cid,
                    currencyId: pc,
                    outcome: "CONFIRMED_AFTER_PATCH",
                    patchAttempts: attempt,
                };
            }
            if (attempt >= exports.CUSTOMER_CURRENCY_PATCH_MAX_ATTEMPTS) {
                break;
            }
            console.log(`${LOG_PREFIX} customer=${cid}${role} currency not visible after PATCH attempt ${attempt}; waiting ${exports.CUSTOMER_CURRENCY_PATCH_RETRY_PAUSE_MS / 1000}s before retry...`);
            await sleep(exports.CUSTOMER_CURRENCY_PATCH_RETRY_PAUSE_MS);
        }
        throw new Error(`${LOG_PREFIX} Could not add subscription currency ${pc} to customer ${cid} after ${exports.CUSTOMER_CURRENCY_PATCH_MAX_ATTEMPTS} PATCH attempt(s)`);
    };
    const ensureIcEliminationCustomersHaveSubscriptionCurrency = async (params) => {
        const currencyId = normalizeCurrencyInternalId(params.subscriptionCurrencyId) ??
            String(params.subscriptionCurrencyId).trim();
        if (!currencyId) {
            throw new Error(`${LOG_PREFIX} invalid subscriptionCurrencyId: ${params.subscriptionCurrencyId}`);
        }
        console.log(`${LOG_PREFIX} ensuring IC elimination customers allow subscription currency ${currencyId}: ` +
            `newSubLineEntity=${params.entityOnNewSubLine} oldSubLineEntity=${params.entityOnOldSubLine}`);
        const newSubLine = await ensureCustomerHasCurrency(params.entityOnNewSubLine, currencyId, { role: "icEliminationNewSubLine" });
        const oldSubLine = await ensureCustomerHasCurrency(params.entityOnOldSubLine, currencyId, { role: "icEliminationOldSubLine" });
        console.log(`${LOG_PREFIX} IC elimination currency ensure complete: ` +
            `newSubLine=${newSubLine.outcome} patchAttempts=${newSubLine.patchAttempts}, ` +
            `oldSubLine=${oldSubLine.outcome} patchAttempts=${oldSubLine.patchAttempts}`);
        return { newSubLine, oldSubLine };
    };
    return {
        ensureCustomerHasCurrency,
        ensureIcEliminationCustomersHaveSubscriptionCurrency,
        getCustomerCurrencyList,
        getCustomerPrimaryCurrencySuiteql,
        patchCustomerAddCurrency,
    };
}
exports.createCustomerCurrencyEnsurer = createCustomerCurrencyEnsurer;
